import { useCallback, useEffect, useState } from 'react';
import { useEsiteCtx } from 'services/esite/context';
import { debounce } from '@cat-ecom/common/utils/commonUtils';
import { DEBOUNCE_TIME_MILLISECONDS } from 'constants/common';
import { useEquipmentSearchSuggestionsQuery } from 'services/equipment-search/use-equipment-search-suggestions-query';
import { EQUIPMENT_SEARCH_FILTER, INTERNAL_SERVER_ERROR } from '../constants';
import { handleSuggestionResults, isExistingSuggestion } from '../utils';

export const useEquipmentSearchSuggestions = (
  { isEditEquipment = false }: { isEditEquipment?: boolean } = {
    isEditEquipment: false
  }
) => {
  const { catalogId, langId, storeId, locale, esiteIdentifier } = useEsiteCtx();
  const [suggestions, setSuggestions] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('');
  const [genericError, setGenericError] = useState<boolean>(false);

  const { data, error, isError, isFetching, isPending, status } =
    useEquipmentSearchSuggestionsQuery({
      catalogId,
      filter,
      langId,
      searchTerm,
      storeId,
      isValid:
        filter === EQUIPMENT_SEARCH_FILTER.SERIAL
          ? searchTerm.length === 8
          : searchTerm.length >= 2,
      isEditEquipment,
      locale,
      esiteIdentifier
    });

  const clearSuggestions = useCallback(() => {
    setSearchTerm('');

    if (suggestions !== null) {
      setSuggestions(null);
    }
  }, [suggestions]);

  useEffect(() => {
    if (!data) {
      clearSuggestions();
      return;
    }
    if (isServerError(data)) {
      clearSuggestions();
      setGenericError(true);
      return;
    }
    if (!isExistingSuggestion(data, suggestions)) {
      setSuggestions(handleSuggestionResults(data));
    }
  }, [data, suggestions, clearSuggestions]);

  function isServerError(data) {
    return data?.status === INTERNAL_SERVER_ERROR;
  }

  const getEquipmentSuggestions = debounce(
    ({ searchTerm, filter }: { searchTerm: string; filter: string }) => {
      setSearchTerm(searchTerm);
      setFilter(filter);
    },
    DEBOUNCE_TIME_MILLISECONDS
  );

  return {
    clearSuggestions,
    getEquipmentSuggestions,
    error,
    isError,
    isFetching,
    isLoading: isPending,
    status,
    suggestions,
    genericError,
    setGenericError
  };
};
